import { useState, useCallback } from 'react';
import { useApi } from '../../hooks';
import airlinerService from '../../services/airlinerService';

/**
 * Custom hook for booking analytics operations
 * @returns {Object} Analytics state and methods
 */
const useBookingAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    bookings: [],
    revenue: {},
    summary: {}
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    endDate: new Date()
  });
  const { loading, error, execute } = useApi();

  const loadAnalytics = useCallback(async (customDateRange = null) => {
    try {
      const range = customDateRange || dateRange;
      const [generalAnalytics, bookingAnalytics, revenueAnalytics] = await Promise.all([
        execute(() => airlinerService.getAnalytics(range)),
        execute(() => airlinerService.getBookingAnalytics(range)),
        execute(() => airlinerService.getRevenueAnalytics(range))
      ]);

      setAnalytics({
        summary: generalAnalytics,
        bookings: bookingAnalytics,
        revenue: revenueAnalytics
      });
    } catch (err) {
      // Failed to load analytics
    }
  }, [execute, dateRange]);

  const updateDateRange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
    loadAnalytics(newDateRange);
  }, [loadAnalytics]);

  const generateReport = useCallback(async (reportType, params = {}) => {
    try {
      return await execute(() => 
        airlinerService.generateReport(reportType, { ...params, ...dateRange })
      );
    } catch (err) {
      throw err;
    }
  }, [execute, dateRange]);

  const downloadReport = useCallback(async (reportId) => {
    try {
      const blob = await execute(() => airlinerService.downloadReport(reportId));
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      throw err;
    }
  }, [execute]);

  return {
    analytics,
    dateRange,
    loading,
    error,
    loadAnalytics,
    updateDateRange,
    generateReport,
    downloadReport
  };
};

export default useBookingAnalytics;